import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Calendar, ChevronLeft, ChevronRight, Clock, FileText, PlayCircle, RefreshCw, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useAgendaMedico } from '@/hooks/useAgendaMedico';
import { useStartClinicalEncounter } from '@/hooks/useConsultasClinicas';
import { LoadingState } from '@/components/shared/LoadingState';
import { ErrorState } from '@/components/shared/ErrorState';
import { EmptyState } from '@/components/shared/EmptyState';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { usePatientContextStore } from '@/stores/patient-context-store';
import type { AgendaMedicoItem } from '@/types/api';

const STATUS_FILTERS = [
  { value: 'todos', label: 'Todos' },
  { value: 'RECEPCAO', label: 'Recepção' },
  { value: 'EM_ATENDIMENTO', label: 'Em atendimento' },
  { value: 'CONFIRMADO', label: 'Confirmados' },
  { value: 'ATENDIDO', label: 'Atendidos' },
  { value: 'CANCELADO', label: 'Cancelados' },
] as const;

type StatusFilter = (typeof STATUS_FILTERS)[number]['value'];

export const Route = createFileRoute('/pep-vida/_authed/agenda')({
  component: AgendaPage,
});

export function AgendaPage() {
  const [data, setData] = useState(todayIso());
  const [busca, setBusca] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('todos');
  const [medicoFilter, setMedicoFilter] = useState('todos');
  const [consultaPendente, setConsultaPendente] = useState<AgendaMedicoItem | null>(null);
  const navigate = useNavigate();
  const startEncounter = useStartClinicalEncounter();
  const setPatient = usePatientContextStore((state) => state.setPatient);
  const cdPacienteBusca = /^\d+$/.test(busca.trim()) ? Number(busca.trim()) : undefined;

  const { data: agenda, isLoading, error, refetch, isRefetching } = useAgendaMedico(
    data,
    cdPacienteBusca,
  );

  const medicoOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of agenda?.items ?? []) {
      const nome = item.medico?.trim();
      if (nome) map.set(nome, nome);
    }
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [agenda?.items]);

  const medicoRows = useMemo(() => {
    const rows = agenda?.items ?? [];
    return medicoFilter === 'todos'
      ? rows
      : rows.filter((item) => item.medico?.trim() === medicoFilter);
  }, [agenda?.items, medicoFilter]);

  const items = useMemo(() => {
    const rows = medicoRows;
    const term = busca.trim().toUpperCase();
    const statusRows =
      statusFilter === 'todos'
        ? rows
        : rows.filter((item) => statusFilterKey(item.statusTexto) === statusFilter);

    if (cdPacienteBusca) return statusRows;
    if (!term) return statusRows;
    return statusRows.filter((item) =>
      [
        item.paciente,
        item.procedimento,
        item.sala,
        item.statusTexto,
        String(item.cdAtendimento ?? ''),
        String(item.cdPaciente),
      ]
        .filter(Boolean)
        .some((value) => String(value).toUpperCase().includes(term)),
    );
  }, [medicoRows, busca, cdPacienteBusca, statusFilter]);

  const statusCounts = useMemo(() => {
    return medicoRows.reduce<Record<StatusFilter, number>>(
      (acc, item) => {
        acc.todos += 1;
        const status = statusFilterKey(item.statusTexto);
        if (status && status in acc) acc[status as StatusFilter] += 1;
        return acc;
      },
      {
        todos: 0,
        RECEPCAO: 0,
        EM_ATENDIMENTO: 0,
        CONFIRMADO: 0,
        ATENDIDO: 0,
        CANCELADO: 0,
      },
    );
  }, [medicoRows]);

  const summary = useMemo(() => {
    const total = medicoRows.length;
    const pendentes = medicoRows.filter((item) =>
      ['RECEPCAO', 'CONFIRMADO', 'AGENDADO'].includes(normalizeStatus(item.statusTexto)),
    ).length;
    const atendidos = statusCounts.ATENDIDO;
    const ocupacao = total > 0 ? Math.round(((atendidos + pendentes) / total) * 100) : 0;
    return { total, pendentes, atendidos, ocupacao };
  }, [medicoRows, statusCounts.ATENDIDO]);

  async function iniciarConsulta(item: AgendaMedicoItem) {
    if (!item.cdAtendimento) return;
    if (normalizeStatus(item.statusTexto) !== 'RECEPCAO') return;
    setPatient({
      cdPaciente: item.cdPaciente,
      cdAtendimento: item.cdAtendimento,
      nome: item.paciente,
      status: item.statusTexto ?? 'Em atendimento',
      meta: [item.idade, item.procedimento].filter(Boolean).join(' | '),
    });
    const encounter = await startEncounter.mutateAsync({
      cd_atendimento: item.cdAtendimento,
      cd_paciente: item.cdPaciente,
    });
    navigate({ to: '/pep-vida/consultas/$id', params: { id: encounter.id } });
  }

  async function retomarConsulta(item: AgendaMedicoItem) {
    if (!item.cdAtendimento) return;
    if (normalizeStatus(item.statusTexto) !== 'EM ATENDIMENTO') return;
    setPatient({
      cdPaciente: item.cdPaciente,
      cdAtendimento: item.cdAtendimento,
      nome: item.paciente,
      status: 'Em atendimento',
      meta: [item.idade, item.procedimento].filter(Boolean).join(' | '),
    });
    // A API faz upsert por cdAtendimento: aqui retomamos o mesmo ClinicalEncounter,
    // preservando SOAP, documentos, telemedicina e horários já registrados.
    const encounter = await startEncounter.mutateAsync({
      cd_atendimento: item.cdAtendimento,
      cd_paciente: item.cdPaciente,
    });
    navigate({ to: '/pep-vida/consultas/$id', params: { id: encounter.id } });
  }

  function solicitarInicio(item: AgendaMedicoItem) {
    if (!item.cdAtendimento || normalizeStatus(item.statusTexto) !== 'RECEPCAO') return;
    setConsultaPendente(item);
  }

  async function confirmarInicio() {
    if (!consultaPendente) return;
    await iniciarConsulta(consultaPendente);
  }

  function visualizarProntuarioFinalizado(item: AgendaMedicoItem) {
    if (!item.clinicalEncounterId) return;
    setPatient({
      cdPaciente: item.cdPaciente,
      cdAtendimento: item.cdAtendimento ?? undefined,
      clinicalEncounterId: item.clinicalEncounterId,
      nome: item.paciente,
      status: 'Finalizada',
      meta: [item.idade, item.procedimento].filter(Boolean).join(' | '),
    });
    navigate({
      to: '/pep-vida/consultas/$id',
      params: { id: item.clinicalEncounterId },
    });
  }

  function abrirProntuario(item: AgendaMedicoItem) {
    setPatient({
      cdPaciente: item.cdPaciente,
      cdAtendimento: item.cdAtendimento ?? undefined,
      nome: item.paciente,
      status: 'Paciente aberto',
      meta: [item.idade, item.procedimento].filter(Boolean).join(' | '),
    });
    navigate({
      to: '/pep-vida/pacientes/$cdPaciente/historico',
      params: { cdPaciente: String(item.cdPaciente) },
    });
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Calendar className="h-4 w-4" />
              Agenda medica
            </CardTitle>
            <DateNavigator data={data} onChange={setData} />
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <SummaryCard label="Atendimentos" value={summary.total} />
            <SummaryCard label="Pendentes" value={summary.pendentes} />
            <SummaryCard label="Atendidos" value={summary.atendidos} />
            <SummaryCard label="Ocupacao" value={`${summary.ocupacao}%`} />
          </div>

          {medicoOptions.length > 1 && (
            <div className="mb-3 max-w-md">
              <Select
                value={medicoFilter}
                onChange={(event) => {
                  setMedicoFilter(event.target.value);
                  setStatusFilter('todos');
                }}
                aria-label="Filtrar por médico"
              >
                <option value="todos">Todos os médicos</option>
                {medicoOptions.map((medico) => (
                  <option key={medico} value={medico}>
                    {medico}
                  </option>
                ))}
              </Select>
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-[180px_1fr_auto] md:items-center">
            <Input
              type="date"
              value={data}
              onChange={(event) => setData(event.target.value)}
              aria-label="Data da agenda"
            />
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={busca}
                onChange={(event) => setBusca(event.target.value)}
                placeholder="Buscar paciente, procedimento, sala, protocolo ou cod. paciente"
                className="pl-9"
                aria-label="Buscar na agenda"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => refetch()}
              disabled={isRefetching}
              aria-label="Atualizar agenda"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isRefetching ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setData(addDays(todayIso(), -1))}>
              Ontem
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setData(todayIso())}>
              Hoje
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setData(addDays(todayIso(), 1))}>
              Amanha
            </Button>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="outline">{agenda?.total ?? 0} atendimentos Clinux</Badge>
            {(busca || statusFilter !== 'todos' || medicoFilter !== 'todos') && (
              <Badge variant="outline">{items.length} filtrados</Badge>
            )}
            {isRefetching && <span>Sincronizando...</span>}
          </div>

          <div className="mt-3 flex flex-wrap gap-2" aria-label="Filtrar por status">
            {STATUS_FILTERS.map((filter) => (
              <Button
                key={filter.value}
                type="button"
                variant={statusFilter === filter.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter(filter.value)}
              >
                {filter.label}
                <span className="ml-2 rounded-full bg-background/20 px-1.5 text-xs">
                  {statusCounts[filter.value]}
                </span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {isLoading && <LoadingState variant="skeleton" />}
      {error && (
        <ErrorState
          message={(error as Error).message}
          onRetry={() => refetch()}
        />
      )}
      {!isLoading && !error && agenda && items.length === 0 && (
        <EmptyState
          title="Nenhum atendimento na agenda"
          description="A agenda do Clinux não retornou atendimentos para esta data."
        />
      )}
      {!isLoading && !error && items.length > 0 && (
        <div className="rounded-md border bg-card">
          <div className="hidden min-w-[1080px] grid-cols-[80px_1fr_120px_1.2fr_1fr_160px_180px] border-b bg-muted/50 px-3 py-2 text-xs font-medium text-muted-foreground md:grid">
            <span>Hora</span>
            <span>Paciente</span>
            <span>Idade</span>
            <span>Procedimento</span>
            <span>Sala</span>
            <span>Status</span>
            <span className="text-right">Ação</span>
          </div>
          <div data-testid="agenda-medico-list">
            {items.map((item) => (
              <AgendaRow
                key={`${item.cdAtendimento ?? item.cdPaciente}-${item.hora ?? item.data}`}
                item={item}
                starting={startEncounter.isPending}
                onStart={() => solicitarInicio(item)}
                onResume={() => retomarConsulta(item)}
                onOpenRecord={() => abrirProntuario(item)}
                onViewFinished={() => visualizarProntuarioFinalizado(item)}
              />
            ))}
          </div>
        </div>
      )}
      <Dialog
        open={Boolean(consultaPendente)}
        title="Iniciar consulta"
        description="Confirme o início do atendimento para registrar o horário de início."
        onClose={() => {
          if (!startEncounter.isPending) setConsultaPendente(null);
        }}
        className="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Deseja iniciar a consulta de{' '}
            <strong className="text-foreground">{consultaPendente?.paciente}</strong>?
          </p>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConsultaPendente(null)}
              disabled={startEncounter.isPending}
            >
              Não
            </Button>
            <Button type="button" onClick={confirmarInicio} disabled={startEncounter.isPending}>
              {startEncounter.isPending ? 'Registrando...' : 'Sim, iniciar consulta'}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

function DateNavigator({
  data,
  onChange,
}: {
  data: string;
  onChange: (data: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button type="button" variant="outline" size="icon" onClick={() => onChange(addDays(data, -1))} aria-label="Dia anterior">
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <div className="min-w-48 rounded-md border bg-background px-3 py-2 text-center">
        <div className="text-sm font-semibold">{formatDateLabel(data)}</div>
        <div className="text-xs text-muted-foreground">{weekdayLabel(data)}</div>
      </div>
      <Button type="button" variant="outline" size="icon" onClick={() => onChange(addDays(data, 1))} aria-label="Proximo dia">
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-md border bg-background px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}

function AgendaRow({
  item,
  starting,
  onStart,
  onResume,
  onOpenRecord,
  onViewFinished,
}: {
  item: AgendaMedicoItem;
  starting: boolean;
  onStart: () => void;
  onResume: () => void;
  onOpenRecord: () => void;
  onViewFinished: () => void;
}) {
  const normalizedStatus = normalizeStatus(item.statusTexto);
  const canStart =
    normalizedStatus === 'RECEPCAO' &&
    Boolean(item.cdAtendimento) &&
    !item.cancelado;
  const canResume =
    normalizedStatus === 'EM ATENDIMENTO' &&
    Boolean(item.cdAtendimento) &&
    !item.cancelado;
  return (
    <div className="grid gap-2 border-b p-3 text-sm last:border-b-0 hover:bg-muted/30 md:min-w-[1080px] md:grid-cols-[80px_1fr_120px_1.2fr_1fr_160px_180px] md:items-center md:px-3 md:py-2">
      <div className="flex items-center gap-2 font-medium">
        <Clock className="h-4 w-4 text-muted-foreground" />
        {item.hora ?? '--:--'}
      </div>
      <div className="min-w-0">
        <div className="truncate font-medium">{item.paciente}</div>
        <div className="mt-1 text-xs text-muted-foreground">#{item.cdAtendimento ?? '-'}</div>
      </div>
      <span className="hidden text-muted-foreground md:block">{item.idade ?? '-'}</span>
      <span className="truncate text-xs md:text-sm">{item.procedimento ?? '-'}</span>
      <span className="truncate text-xs text-muted-foreground md:text-sm">{item.sala ?? '-'}</span>
      <div className="flex flex-wrap items-center gap-1">
        {item.statusTexto && (
          <Badge variant={statusBadgeVariant(normalizedStatus)}>
            {formatStatusLabel(item.statusTexto)}
          </Badge>
        )}
        {item.retorno && <Badge variant="outline">Retorno</Badge>}
        {item.encaixe && <Badge variant="outline">Encaixe</Badge>}
        {item.urgente && <Badge variant="destructive">Urgente</Badge>}
      </div>
      <div className="flex justify-start gap-2 md:justify-end">
        {!canResume && <Button type="button" variant="outline" size="sm" onClick={normalizedStatus === 'FINALIZADO' ? onViewFinished : onOpenRecord} disabled={normalizedStatus === 'FINALIZADO' && !item.clinicalEncounterId} aria-label="Prontuario" title="Prontuario"><FileText className="h-4 w-4" />{'Prontu\u00e1rio'}</Button>}
        {canStart && (
          <Button
            variant="default"
            size="sm"
            onClick={onStart}
            disabled={starting}
            aria-label="Iniciar consulta"
            title="Iniciar consulta"
          >
            <PlayCircle className="h-4 w-4" />
            Iniciar consulta
          </Button>
        )}
        {canResume && (
          <Button
            variant="default"
            size="sm"
            onClick={onResume}
            disabled={starting}
            aria-label="Retomar consulta"
            title="Retomar consulta"
          >
            <PlayCircle className="h-4 w-4" />
            Retomar consulta
          </Button>
        )}
      </div>
    </div>
  );
}

function todayIso() {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
}

function addDays(iso: string, days: number) {
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 10);
}

function formatDateLabel(iso: string) {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('pt-BR');
}

function weekdayLabel(iso: string) {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('pt-BR', { weekday: 'long' });
}

function normalizeStatus(status: string | null | undefined) {
  return String(status ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();
}

function statusBadgeVariant(
  status: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'CANCELADO') return 'destructive';
  if (status === 'EM ATENDIMENTO' || status === 'EM SALA') return 'default';
  if (status === 'ATENDIDO' || status === 'FINALIZADO') return 'secondary';
  return 'outline';
}

function formatStatusLabel(status: string | null | undefined) {
  const normalized = normalizeStatus(status);
  const labels: Record<string, string> = {
    RECEPCAO: 'Recepção',
    CONFIRMADO: 'Confirmado',
    ATENDIDO: 'Atendido',
    CANCELADO: 'Cancelado',
    'EM SALA': 'Em atendimento',
    'EM ATENDIMENTO': 'Em atendimento',
    FINALIZADO: 'Finalizado',
  };
  return labels[normalized] ?? status ?? '—';
}

function statusFilterKey(status: string | null | undefined): StatusFilter | '' {
  const normalized = normalizeStatus(status);
  if (normalized === 'EM SALA' || normalized === 'EM ATENDIMENTO') {
    return 'EM_ATENDIMENTO';
  }
  if (
    normalized === 'RECEPCAO' ||
    normalized === 'CONFIRMADO' ||
    normalized === 'ATENDIDO' ||
    normalized === 'CANCELADO'
  ) {
    return normalized;
  }
  return '';
}
